import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { unwrap } from '@/api/envelope';
import { updateBillingContact, type BillingResponse } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { ContactFields } from '@/components/form/ContactFields';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { useReadOnly } from '@/auth/useViewingAs';
import { useToast } from '@/components/ui/useToast';

import {
  contactSchema,
  toContactPayload,
  toContactValues,
  type ContactInput,
  type ContactOutput,
} from './billingContact';
import { billingKey } from './keys';

interface BillingContactFormProps {
  record: BillingResponse;
  /** True while the organization is suspended or deactivated. */
  readOnly: boolean;
}

/**
 * The server's field names to the form's, in page order. The contact is
 * nested (`billing_contact.email`), and the server names a refused field by
 * the last part of its path alone.
 */
const SERVER_FIELDS = {
  name: 'name',
  title: 'title',
  email: 'email',
  phone: 'phone',
} as const;

/**
 * Who Activist Technologies sends the invoices to - the one thing on the
 * Billing page the organization may change.
 *
 * Save is offered only once something has changed, so a click cannot send the
 * same contact back. A failure keeps what was typed and says why in the
 * backend's words: a 403 is the account's status, a 422 names the field.
 */
export function BillingContactForm({
  record,
  readOnly: statusForbids,
}: BillingContactFormProps) {
  // Read here rather than trusted from the page, so the form is read-only
  // while staff view the organization wherever it is used.
  const viewing = useReadOnly();
  const readOnly = statusForbids || viewing;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [failure, setFailure] = useState<string | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<ContactInput, unknown, ContactOutput>({
    resolver: zodResolver(contactSchema),
    defaultValues: toContactValues(record.billing_contact),
  });

  // Moved to, so it is announced rather than waiting to be found.
  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  // The same flag the Save button reads, so the prompt and the button cannot
  // disagree about whether anything is unsaved.
  const unsaved = useUnsavedChanges(isDirty);

  const save = useMutation({
    mutationFn: async (values: ContactOutput) => {
      const reply = await updateBillingContact({
        body: { billing_contact: toContactPayload(values, record.billing_contact) },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async (stored) => {
      // What the server stored, not what was typed. Resetting to it is also
      // what makes Save unavailable again until the next change.
      if (stored) {
        reset(toContactValues(stored.billing_contact));
        queryClient.setQueryData(billingKey, stored);
      }
      toast.show({ message: 'Billing contact saved.' });
      await queryClient.invalidateQueries({ queryKey: billingKey });
    },
    onError: (error) => {
      // Each field's message lands on its field; whatever concerns no field -
      // the account's status, a fault - goes to the alert above the form.
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
      );
      if (remainder) setFailure(remainder);
    },
  });

  return (
    <Stack
      component="form"
      spacing={2}
      aria-label="Billing contact"
      data-testid="billing-contact-form"
      // The browser's own bubbles would pre-empt the messages each field shows.
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => save.mutate(values))(event);
      }}
    >
      {failure ? (
        <Alert
          severity="error"
          role="alert"
          ref={alertRef}
          tabIndex={-1}
          data-testid="billing-contact-form-error"
        >
          {failure}
        </Alert>
      ) : null}

      <ContactFields
        labelPrefix="Billing"
        names={{ name: 'name', title: 'title', email: 'email', phone: 'phone' }}
        register={register}
        errors={errors}
        disabled={readOnly}
        testId="billing-contact-form-contact"
      />

      {/* Not rendered at all while the account is read-only: a Save that can
          never be used is a control that lies about what the page allows. */}
      {readOnly ? null : (
        <Box>
          <Button
            type="submit"
            disabled={!isDirty}
            loading={save.isPending}
            data-testid="billing-contact-form-save-button"
          >
            Save billing contact
          </Button>
        </Box>
      )}

      <UnsavedChangesDialog {...unsaved} />
    </Stack>
  );
}
